import { pipe, rawTransform } from 'valibot';
import { MixedOneSearchPreferenceResponse } from '../../types/one-search/onesearch-response/OfferTeaserResponseDto';
import { MixedOneSearchSearchResponse } from '../../types/one-search/onesearch-response/SearchResponseDto';
import { handleSrpUrl } from '../../util/HandleUrls';
import { SchemaAny } from '../VehicleTileDataSchema';
import { SimplePriceVehicleDataFormat } from '../simple-price/SimplePriceTransformer';

import { appendUserContextToUrl } from '../../util/EnhanceUrlWithCtx';
import { generatePrices } from '../helper/prices';

export const ConvertOneSearchSearchToSimplePriceVehicleDataTransformer = pipe(
  SchemaAny,
  rawTransform<MixedOneSearchSearchResponse, SimplePriceVehicleDataFormat>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in ConvertOneSearchSearchToPreferenceTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as unknown as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }

    const url = handleSrpUrl(
      dataset.value.dlsUrl,
      dataset.value.rawData?.ctaUrl,
      {
        sortByLeasingrate: dataset.value.rawData?.sortByLeasingrate,
      },
      dataset.value.ctx,
    );
    const dcpProductType = dataset.value?.identification?.dcpProductType;
    if (!dcpProductType) {
      addIssue({
        message: 'dcpProductType is not available',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }

    return {
      heading: dataset.value.rawData?.tileHeadline !== null ? dataset.value.rawData?.tileHeadline : '',
      description: dataset.value.rawData?.tileDescription !== null ? dataset.value.rawData?.tileDescription : '',
      image:
        dataset.value.rawData?.croppedimage !== null
          ? {
              url: dataset.value.rawData?.croppedimage?.sources?.mq1,
              alt:
                dataset.value.rawData?.croppedimage?.description !== null
                  ? dataset.value.rawData?.croppedimage?.description
                  : '',
              title:
                dataset.value.rawData?.croppedimage?.title !== null ? dataset.value.rawData?.croppedimage?.title : '',
            }
          : undefined,

      cta: {
        label: dataset.value.rawData?.ctaLabel,
        srp: url,
      },
      tag: {
        badges: [
          {
            showIcon: false,
            badges: dataset.value.rawData?.recommendationBadge
              ? {
                  name: dataset.value.rawData?.recommendationBadge?.name,
                  description:
                    dataset.value.rawData?.recommendationBadge?.description !== null
                      ? dataset.value.rawData?.recommendationBadge?.description
                      : '',
                  footnote:
                    dataset.value.rawData?.recommendationBadge?.footnote !== null
                      ? dataset.value.rawData?.recommendationBadge?.footnote
                      : '',
                }
              : {},
          },
        ],
      },
      prices: {
        prices: generatePrices({
          productCode: dataset.value.identification?.code,
          commissionNumber: dataset.value.identification?.commissionNumber,
          dcpProductType: dcpProductType,
          market: dataset.value.market,
          locale: dataset.value.lang,
          currency: dataset.value.price?.currency,
          priceRawValue: undefined, // TODO:  dataset.value.price?.grossValue ?? no clue, what data we should use. No prices in de_AT. Call fails in en_MY
        }),
      },
      mixedPreferenceResponse: {
        productCode: dataset.value?.identification?.code,
        vehicleName: dataset.value?.vehicleModel?.name,
        vehicleImages: dataset.value.images?.exterior,
        technicalInformation: dataset.value?.technicalInformation,
        dcpProductType: dataset.value?.identification?.dcpProductType,
        productCategory: dataset.value?.vehicleModel?.category.value,
        commissionNumber: dataset.value?.identification?.commissionNumber,
        srpUrl: dataset.value.dlsUrl,
        mode: dataset.value.mode,
        market: dataset.value.market,
        lang: dataset.value.lang,
        translations: dataset.value.translations,
        brand: dataset.value.brand,
        responseTypeId: 'OfferTeaserDto',
      },
      common: {
        common: {
          hasCriticalError: false,
          brand: dataset.value.brand,
          productType: 'STOCK',
          bm6: undefined,
          modelIdentifier: undefined,
          vehicleName: dataset.value?.vehicleModel?.name,
          identification: {
            commissionNumber: dataset.value.identification?.commissionNumber,
            productCode: dataset.value?.identification?.code ?? 'no code',
            configurationId: undefined,
          },
          isVan: false,
        },
      },
    };
  }),
);

export const ConvertOneSearchSearchToPreferenceTransformer = pipe(
  SchemaAny,
  rawTransform<MixedOneSearchSearchResponse, MixedOneSearchPreferenceResponse>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in ConvertOneSearchSearchToPreferenceTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as unknown as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return {
      productCode: dataset.value?.identification?.code,
      vehicleName: dataset.value?.vehicleModel?.name,
      vehicleImages: dataset.value.images?.exterior,
      technicalInformation: dataset.value?.technicalInformation,
      dcpProductType: dataset.value?.identification?.dcpProductType,
      productCategory: dataset.value?.vehicleModel?.category.value,
      commissionNumber: dataset.value?.identification?.commissionNumber,
      srpUrl: appendUserContextToUrl(dataset.value.dlsUrl, dataset.value.ctx),
      mode: dataset.value.mode,
      market: dataset.value.market,
      lang: dataset.value.lang,
      translations: dataset.value.translations,
      brand: dataset.value.brand,
      responseTypeId: 'OfferTeaserDto',
    };
  }),
);
